import { Component, EventEmitter, Input, Output } from '@angular/core';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { QtBaseData, QtTemplateType } from '@models/qt-templates.model';
import { Subject } from 'rxjs/Subject';
import { Contract, Product, Summary } from '@models/models';
import { DragulaService } from 'ng2-dragula';
import { filter, map, takeUntil } from 'rxjs/operators';
import { QuotesDataProvider } from '@services/quote/quotes-data-service/quotes.data.service';

@Component({
	selector: 'qt-fixed-template-control',
	template: require('./qt-fixed-template-control.component.html'),
	styles: [require('./qt-fixed-template-control.component.scss')]
})
export class QtFixedTemplateControlComponent {
	@Input() templateData: QtBaseData;
	@Input() type: QtTemplateType;
	@Input() disableContractButton: boolean;
	@Input() generateInvoiceDisabled: boolean;
	@Output() emitContractChange: EventEmitter<string> = new EventEmitter();
	@Output() emitContractToEdit: EventEmitter<Contract> = new EventEmitter();
	@Output() emitPaymentChange: EventEmitter<string> = new EventEmitter();

	dragulaAddonsOptions = {
		moves: (el, container, handle): boolean =>
			handle.classList.contains('handle-package')
	};
	dragulaDiscountsOptions = {
		moves: (el, container, handle): boolean =>
			handle.classList.contains('handle-package')
	};

	templateType: string;
	notesPlaceholder: string;
	paymentScheduleText: string;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private dragulaService: DragulaService,
		private quoteDataService: QuotesDataProvider,
		private quoteUpdateService: QuoteUpdateService
	) {}

	ngOnInit(): void {
		this.setDragulaOptions();
		this.reactToProductPropertiesChange();
		this.templateType = this.type === 'QUOTE' ? 'Quote' : 'Invoice';
		this.notesPlaceholder = `These are additional notes you wish to add to your ${this.templateType.toLowerCase()}.`;
		this.paymentScheduleText = `Assign a payment schedule to this ${this.templateType.toLowerCase()}`;
	}

	ngOnDestroy(): void {
		this.clear();
	}

	addNewAddon(): void {
		const item = QuoteUpdateService.setEmptyProduct('ADD_ON');
		item.mandatory = true;
		this.templateData.addons.items.push(item);
	}

	setNewFixedTemplateAddon(data: { index: number; product: Product }): void {
		this.templateData.addons.items.forEach(
			(packageItem: Product, index: number): void => {
				if (index === data.index) {
					this.templateData.addons.items[index] = JSON.parse(
						JSON.stringify(
							Object.assign({}, data.product, {
								mandatory: true,
								type: 'ADD_ON'
							})
						)
					);
				}
			}
		);
		this.updateTemplateSummary();
	}

	removeFixedTemplateAddon({ index }): void {
		this.templateData.addons.items.splice(index, 1);
		this.updateTemplateSummary();
	}

	addNewFixedTemplateDiscount(): void {
		this.templateData.discounts.items.push(
			QuoteUpdateService.setEmptyProduct('GIFT')
		);
	}

	setNewFixedTemplateDiscount(data: { index: number; product: Product }): void {
		this.templateData.discounts.items.forEach(
			(packageItem: Product, index: number): void => {
				if (index === data.index) {
					this.templateData.discounts.items[index] = JSON.parse(
						JSON.stringify(Object.assign({}, data.product))
					);
				}
			}
		);
		this.updateTemplateSummary();
	}

	removeFixedTemplateDiscount(index: number): void {
		this.templateData.discounts.items.splice(index, 1);
		this.updateTemplateSummary();
	}

	onPaymentScheduleChange(templateId: string): void {
		this.templateData.paymentSchedulerTemplateId = templateId;
		switch (templateId) {
			case '-2':
				this.templateData.paymentsStrategy = 'NO_SPLIT_PAYMENTS';
				break;
			case '-3':
				this.templateData.paymentsStrategy = 'NO_DUE_DATE';
				break;
			default:
				this.templateData.paymentsStrategy = 'BY_PAYMENT_SCHEDULER';
				break;
		}
		this.emitPaymentChange.emit(templateId);
	}

	setQuestionnaire(templateId: string): void {
		this.templateData.questionnaireTemplateId = templateId;
	}

	setContract(templateId: string): void {
		this.emitContractChange.emit(templateId);
	}

	emitContractEdit(): void {
		this.emitContractToEdit.emit();
	}

	private reactToProductPropertiesChange(): void {
		this.quoteUpdateService.updatedProductObservable
			.pipe(
				takeUntil(this.unsubscribe),
				filter((product): boolean => product !== null),
				map((): Array<Product> => this.getMergedItems())
			)
			.subscribe((items: Array<Product>): void => {
				if (this.quoteUpdateService.validateProductsPropsDefined(items)) {
					this.updateTemplateSummary();
				}
			});
	}

	private updateTemplateSummary(): void {
		// setTimeout fixes issue on Safari when id of product changes with delay
		setTimeout((): void => {
			this.quoteDataService
				.calculateProductsSummary(
					this.getMergedItems(),
					this.templateData.currency.percentage,
					this.templateData.currency.currencyDTO.symbol
				)
				.pipe(takeUntil(this.unsubscribe))
				.subscribe(
					(data: Summary): Summary =>
						(this.templateData.summary = JSON.parse(JSON.stringify(data)))
				);
		}, 0);
	}

	private getMergedItems(): Array<Product> {
		return [
			...this.templateData.addons.items,
			...this.templateData.discounts.items
		];
	}

	private setDragulaOptions(): void {
		this.dragulaService.setOptions('addons', this.dragulaAddonsOptions);
		this.dragulaService.setOptions('discounts', this.dragulaDiscountsOptions);
	}

	private clear(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.dragulaService.destroy('addons');
		this.dragulaService.destroy('discounts');
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/quote-invoice-templates/qt-templates-update/qt-fixed-template-control/qt-fixed-template-control.component.ts